<template>
    <div class="page mt-4 px-3 px-md-0">
        <NavigationsBreadcrumb :items="breadcrumbs" class="mt-0" />
        <div class="counselling-detail-page">
            <div class="counselling-detail-page__detail">
                <div class="mb-3 mb-md-4">
                    <nuxt-link class="counselling-detail-page__go-back mb-2 mb-md-0" :to="$t('counselling.homepageLink')">{{
                        $t('counselling.backToCounsellingHomepage')
                    }}</nuxt-link>
                    <button class="counselling-detail-page__ask-question" @click="openUpsertModal">
                        {{ $t('counselling.askQuestion') }}
                    </button>
                </div>

                <ElementsCounsellingDetail :counselling-post="response.mainQuestion" />

                <div
                    v-if="response.recommendedProducts.length > 0 || response.recommendedCategories.length > 0"
                    class="counselling-detail-page__recommendations"
                >
                    <template v-if="response.recommendedProducts.length > 0">
                        <h2 class="counselling-detail-page__title mt-5">{{ $t('counselling.recommendations.productsTitle') }}</h2>
                        <ElementsCarouselBox
                            :products="response.recommendedProducts"
                            :show-pre-basket="true"
                            item-list-name="Counselling_detail_recommendation"
                            :type-item="'slim-carousel__item--addon'"
                            :min-products-arrow-slider-count="4"
                        />
                    </template>

                    <template v-if="response.recommendedCategories.length > 0">
                        <h2 class="counselling-detail-page__title mt-5">{{ $t('counselling.recommendations.categoriesTitle') }}</h2>
                        <ul class="recommended-categories-container">
                            <template v-for="category in response.recommendedCategories" :key="category.name">
                                <SubcategoryItem :subcategory="category" />
                            </template>
                        </ul>
                    </template>
                </div>
            </div>
            <div class="counselling-detail-page__sidebar">
                <template v-if="response.relatedArticles.length > 0">
                    <h2 class="counselling-detail-page__title mt-5">{{ $t('counselling.articles') }}</h2>
                    <LayoutArticleList :articles="response.relatedArticles" :cols-md="1" :cols-lg="1" :image-on-left="true" />
                    <nuxt-link class="btn btn-outline-primary py-1 px-4" :to="$t('articles.url')">{{
                        $t('counselling.allArticles')
                    }}</nuxt-link>
                </template>

                <h2 class="counselling-detail-page__title mt-5 mb-4">{{ $t('counselling.mostSearchedTopics') }}</h2>
                <nuxt-link
                    v-for="(topic, index) in response.mostSearchedTopics"
                    :key="index"
                    class="counselling-detail-page__tag"
                    :to="`${$t('counselling.listLink')}${topic.shopUrlPath}`"
                >
                    {{ topic.title }}
                </nuxt-link>
            </div>
            <div class="counselling-detail-page__questions">
                <template v-if="response.similarQuestions.length > 0">
                    <h2 class="counselling-detail-page__title mt-4">{{ $t('counselling.similarQuestions') }}</h2>
                    <ElementsCounsellingList :counselling-posts="response.similarQuestions" />
                </template>

                <template v-if="response.recentQuestions.length > 0">
                    <h2 class="counselling-detail-page__title mt-5">{{ $t('counselling.recentQuestions') }}</h2>
                    <ElementsCounsellingList :counselling-posts="response.recentQuestions" />
                </template>
            </div>
        </div>
        <ElementsAskYourPharmacist class="mb-4" />
        <ElementsCounsellingUpsertModal />
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useNuxtApp } from '#app'
import ViewCounsellingDetailResponse from '~/models/Api/Counselling/ViewCounsellingDetailResponse'
import GtmPageData from '~/models/DataLayer/GtmPageData'
import DataLayer from '~/extensions/DataLayer'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { ResponseStatus } from '~/models/Api/ResponseStatus'
import SubcategoryItem from '~/components/objects/SubcategoryItem.vue'
import useIsPartOfJsonLd from '~/composables/useIsPartOfJsonLd'

const route = useRoute()
const params = route.matched[0].props.default as { [name: string]: string }
const id = parseInt(params.id)

const { data } = await useAsyncData('count', () => useNuxtApp().$repository.getCounsellingDetail(id))

if (!data.value) {
    throw createError({
        statusCode: ResponseStatus.INTERNAL_SERVER_ERROR,
        statusMessage: 'Internal server error. Invalid response from API',
    })
}

const response = ref<ViewCounsellingDetailResponse>(data.value)

if (response.value.mainQuestion === null) {
    throw createError({
        statusCode: ResponseStatus.NOT_FOUND,
        statusMessage: 'Not found',
    })
}

const i18n = useI18n()

const breadcrumbs = ref([
    {
        title: i18n.t('counselling.title'),
        url: i18n.t('counselling.homepageLink'),
    },
    {
        title: i18n.t('counselling.askPharmacist'),
        url: i18n.t('counselling.listLink'),
    },
    {
        title: response.value.mainQuestion.title,
        url: response.value.mainQuestion.url,
    },
])

const config = useRuntimeConfig()

useHead({
    title: response.value.mainQuestion.title,
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: response.value.mainQuestion.question,
        },
    ],
    link: [
        {
            rel: 'canonical',
            href: `${config.public.apiUrl}${response.value.mainQuestion.url}`,
        },
    ],
})

const triggerPageView = (): void => {
    const page = {
        type: 'poradna',
        title: response.value.mainQuestion.title,
    } as unknown as GtmPageData

    DataLayer.pushPageViewToDataLayer(page)
    LuigisBoxEventApi.sendPageView()
}

onMounted(() => {
    triggerPageView()
})

const openUpsertModal = () => {
    useNuxtApp().$bus.$emit('showCounsellingUpsertModal')
}

const jsonldFunction = () => {
    const postUrl = `${config.public.apiUrl}${response.value.mainQuestion.url}`

    return {
        '@context': 'https://schema.org/',
        '@type': 'QAPage',
        headline: response.value.mainQuestion.title,
        url: postUrl,
        mainEntity: {
            '@type': 'Question',
            name: response.value.mainQuestion.title,
            text: response.value.mainQuestion.question,
            answerCount: 1,
            datePublished: response.value.mainQuestion.createdAtDateIso8601,
            '@id': `${postUrl}#questionandanswer`,
            acceptedAnswer: {
                '@type': 'Answer',
                text: response.value.mainQuestion.answer.replace(/<[^>]*>?/gm, '').replace(/(\r\n|\n|\r)/gm, ''),
                url: postUrl,
                author: {
                    '@type': 'Person',
                    name: response.value.mainQuestion.author.name,
                    url: `${config.public.apiUrl}${response.value.mainQuestion.author.url}`,
                    image: response.value.mainQuestion.author.image,
                    worksFor: { '@id': `${config.public.apiUrl}#publisher` },
                },
                datePublished: response.value.mainQuestion.createdAtDateIso8601,
            },
        },
        inLanguage: i18n.t('locale'),
        isPartOf: useIsPartOfJsonLd(),
        potentialAction: {
            '@type': 'ReadAction',
            object: {
                '@type': 'Question',
                name: response.value.mainQuestion.title,
                '@id': `${postUrl}#questionandanswer`,
            },
        },
        keywords: response.value.mainQuestion.title,
        breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.value
                .map((item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@id': `${config.public.apiUrl}${item.url}`,
                        name: item.title,
                    },
                }))
                .filter((breadcrumb) => breadcrumb.item.name.length > 1),
        },
    } as any
}
useJsonld(jsonldFunction())
</script>

<style lang="scss" scoped>
@import '../../node_modules/bootstrap/scss/functions';
@import '../../node_modules/bootstrap/scss/variables';
@import '../../node_modules/bootstrap/scss/mixins';

.counselling-detail-page {
    display: grid;
    grid-gap: 1.5em;
    gap: 1.5em;
    grid-template-columns: 100%;
    grid-template-areas:
        'detail'
        'sidebar'
        'questions';

    @include media-breakpoint-up(lg) {
        grid-template-columns: 60% auto;
        grid-template-areas:
            'detail sidebar'
            'questions sidebar';
    }

    &__title {
        font-size: 20px;
        font-weight: 450;
    }

    &__detail {
        grid-area: detail;
    }

    &__recommendations {
        grid-area: recommendations;
    }

    &__sidebar {
        grid-area: sidebar;
    }

    &__questions {
        grid-area: questions;
    }

    &__tag {
        background: $color-peach;
        border-radius: 100px;
        color: $color-dark;
        padding: 12px 18px;
        font-size: 14px;
        font-weight: 590;
        margin: 0 12px 12px 0;
        display: inline-block;

        &:hover {
            background: darken($color-peach, 3%);
        }
    }

    &__go-back {
        border-radius: 10px;
        background: $color-grey;
        display: inline-block;
        color: $color-dark;
        padding: 0 13px 0 30px;
        font-size: 14px;
        line-height: 36px;
        font-weight: 590;
        margin-right: 30px;
        margin-bottom: 10px;
        position: relative;

        &:hover {
            background: darken($color-grey, 3%);
        }

        &:before {
            border: 1px solid $color-dark;
            border-left: 0;
            border-bottom: 0;
            content: '';
            position: absolute;
            left: 15px;
            top: 13px;
            display: block;
            width: 10px;
            height: 10px;
            transform: rotate(225deg);
            transition: all 200ms ease;
        }
    }

    &__ask-question {
        border-radius: 100px;
        background: $color-cta;
        padding: 0 10px;
        font-size: 14px;
        color: $color-white;
        border: 0;
        line-height: 36px;
        width: 100%;
        font-weight: 590;
        cursor: pointer;

        &:hover {
            background: darken($color-cta, 3%);
        }

        @include media-breakpoint-up(lg) {
            width: 300px;
        }
    }
}

.recommended-categories-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    justify-content: center;
    padding: 0;
    margin: 0 auto;

    @include screen-sm {
        grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
    }

    @media (max-width: 320px) {
        grid-template-columns: auto;
    }
}
</style>
